import { Tab, Tabs } from 'react-bootstrap';
import { useEffect, useState } from 'react';
import UserRequests from 'components/global/UserRequests';
import UserInvitations from 'components/global/UserInvitations';
import OrganizationsList from 'components/global/OrganizationsList/OrganizationsList';
import BreadCrumbWrapper from 'components/global/BreadCrumbs/BreadCrumbs';
import resetSearchParams from '../../../../utils/resetSearchParams';
import { useLocation } from 'react-router';
import EdoProgramRequests from '../EdoLenders/components/ProgramRequests/EdoProgramRequests';
import ProgramRequestDetailModal from '../../../global/ProgramRequestDetail/ProgramRequestDetailModal';
import { locationSearchToObject } from '../../../../libs/functions/global.functions';
import { checkEdoPermissionsAcrossPrograms } from '../../../../libs/edoPermissions';
import userAccountSignal from '../../../../signals/UserAccount.signal';
import api from '../../../../api/api';
import loaderSignal from '../../../../signals/Loader.signal';

const EdoOrganizationSummary = ({ portalType }) => {
  const { pathname, search } = useLocation();
  const tab = new URLSearchParams(search).get('tab');
  const [canViewMaintenance, setCanViewMaintenance] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  // Check if user has canViewMaintenance permission on any program
  useEffect(() => {
    const checkViewMaintenancePermission = async () => {
      try {
        setIsChecking(true);
        loaderSignal.update({ isContentLoading: true });
        const userData = userAccountSignal.value.userData;
        
        // Wait for userData to be loaded
        if (!userData) {
          setIsChecking(false);
          loaderSignal.reset();
          return;
        }

        // Fetch funding programs for the EDO
        const fundingPrograms = await api.get({
          path: '/fundingPrograms',
          options: {
            where: {
              edoId: userData?.account?.edo?.id,
            },
          },
        });

        const permissions = await checkEdoPermissionsAcrossPrograms(
          fundingPrograms,
          ['canViewMaintenance']
        );
        setCanViewMaintenance(permissions.canViewMaintenance);
      } catch (error) {
        console.error('Error checking view maintenance permission:', error);
        setCanViewMaintenance(false);
      } finally {
        setIsChecking(false);
        loaderSignal.reset();
      }
    };

    checkViewMaintenancePermission();
  }, [userAccountSignal.value.userData]);

  if (isChecking) {
    return (
      <BreadCrumbWrapper>
        <div className="bg-white p-32 border border-gray shadow-sm">
          <p className="text-center">Loading...</p>
        </div>
      </BreadCrumbWrapper>
    );
  }

  if (!canViewMaintenance) {
    return (
      <BreadCrumbWrapper>
        <div className="bg-white p-32 border border-gray shadow-sm">
          <h3 className="text-center">Access Denied</h3>
          <p className="text-center">
            You do not have permission to view this maintenance data.
          </p>
        </div>
      </BreadCrumbWrapper>
    );
  }

  return (
    <BreadCrumbWrapper>
      <Tabs
        defaultActiveKey={tab || 'all'}
        mountOnEnter
        onSelect={(tab) => resetSearchParams({ tab })}
      >
        <Tab eventKey="all" title="All">
          <div className="bg-white p-32 border border-gray shadow-sm">
            <OrganizationsList
              refetchingFilter={({ search }) => {
                const { tab } = locationSearchToObject(search);
                return tab === 'all' || !tab;
              }}
              newPath={location.pathname}
            />
          </div>
        </Tab>
        {(pathname.includes('vc') || pathname.includes('lender')) && (
          <Tab eventKey="programRequests" title="Program Requests">
            <div className="bg-white p-32 border border-gray shadow-sm">
              <EdoProgramRequests
                refetchingFilter={({ search }) => {
                  const { tab } = locationSearchToObject(search);
                  return tab === 'programRequests';
                }}
              />
            </div>
          </Tab>
        )}
        <Tab eventKey="userRequests" title="Expressed Interest">
          <div className="bg-white p-32 border border-gray shadow-sm">
            <UserRequests
              refetchingFilter={({ search }) => {
                const { tab } = locationSearchToObject(search);
                return tab === 'userRequests';
              }}
            />
          </div>
        </Tab>
        <Tab eventKey="userInvitations" title="Invitations">
          <div className="bg-white p-32 border border-gray shadow-sm">
            <UserInvitations
              organizationType={portalType}
              refetchingFilter={({ search }) => {
                const { tab } = locationSearchToObject(search);
                return tab === 'userInvitations';
              }}
            />
          </div>
        </Tab>
      </Tabs>
      <ProgramRequestDetailModal />
    </BreadCrumbWrapper>
  );
};

export default EdoOrganizationSummary;
